import { useNavigateWithParams } from '@experiences/util';
import { Box } from '@mui/material';
import { ApButton } from '@uipath/portal-shell-react';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useUiStepper } from './UiStepperContextProvider';

interface UiStepNavigationProps {
    nextLabel?: string;
    backLabel?: string;
    onNext?: () => void; // Optional custom next handler
    showCancel?: boolean;
    cancelRoute?: string; // Route to navigate to on cancel
    cancelLabel?: string;
}

export const UiStepNavigation: React.FC<UiStepNavigationProps> = ({
    nextLabel,
    backLabel,
    onNext,
    showCancel = true,
    cancelRoute,
    cancelLabel,
}) => {
    const { formatMessage: translate } = useIntl();
    const navigate = useNavigateWithParams();

    const {
        nextStep,
        prevStep,
        isFirstStep,
        currentStep,
        stepHooks,
        isLoading,
    } = useUiStepper();

    // Get disableNext/disableBack from step hooks
    const stepHooksForCurrentStep = stepHooks[currentStep];
    const disableNext = stepHooksForCurrentStep?.disableNext ?? false;
    const disableBack = stepHooksForCurrentStep?.disableBack ?? false;

    const handleNext = useCallback(() => {
        if (!disableNext && !isLoading) {
            if (onNext) {
                onNext();
            } else {
                // nextStep() will call onComplete if it's the last step
                nextStep();
            }
        }
    }, [ disableNext, isLoading, onNext, nextStep ]);

    const handleBack = useCallback(() => {
        if (!disableBack && !isFirstStep) {
            prevStep();
        }
    }, [ disableBack, isFirstStep, prevStep ]);

    const handleCancel = useCallback(() => {
        if (cancelRoute) {
            navigate(cancelRoute);
        }
    }, [ navigate, cancelRoute ]);

    return (
        <Box
            sx={{
                padding: '24px',
                display: 'flex',
                flexDirection: 'row',
                gap: '10px',
                justifyContent: 'flex-end',
                alignItems: 'center',
                borderTop: '1px solid var(--color-border-de-emp)',
            }}
            data-cy="ui-step-navigation"
        >
            {showCancel && <ApButton
                variant="secondary"
                onClick={handleCancel}
                data-cy="step-cancel-button"
                label={cancelLabel ?? translate({ id: 'CLIENT_CANCEL' })}
            />}
            {!isFirstStep && <ApButton
                variant="secondary"
                onClick={handleBack}
                disabled={disableBack}
                data-cy="step-back-button"
                label={backLabel ?? translate({ id: 'CLIENT_BACK' })}
            />}
            <ApButton
                variant="primary"
                onClick={handleNext}
                disabled={disableNext || isLoading}
                loading={isLoading}
                data-cy="step-next-button"
                label={nextLabel ?? stepHooks[currentStep]?.nextLabel ?? translate({ id: 'CLIENT_NEXT' })}
            />
        </Box>
    );
};
